// What happens when one token stands on another. Three kinds of thing get
// confused with each other, so each is named and each has one rule:
//
//   creature    the ordinary case. A SMALLER creature that moves onto a
//               LARGER one has climbed on; the larger one is the base and
//               carries it. Nesting dolls: the base may itself be riding
//               something bigger, and the whole stack moves with the
//               biggest thing in it.
//   mount       ridden by anything, whatever the sizes. Move onto it and
//               you are aboard — the size test is skipped, because a
//               halfling's pony is not bigger than the halfling by area and
//               a warhorse should still pick up the knight.
//   walks over  a creature that moves OVER others rather than carrying
//               them: the tarrasque the party is fighting from underneath.
//               Never a base. Whoever is in its squares is beneath it.
//   emanation   a spell's area, not a creature. It follows the caster it
//               belongs to, stays centred on them, and never dismounts or
//               becomes a base for anybody.
//
// Riders are seated so every one of them stays visible: one in the middle,
// two side by side, three or more in a ring. Ten characters on a tarrasque
// should read as ten characters on a tarrasque.
import type { Layout } from "./types";

export type TokenRole = "creature" | "mount" | "under" | "emanation";
export const TOKEN_ROLES: { value: TokenRole; label: string; hint: string }[] = [
  { value: "creature", label: "creature", hint: "The usual. A smaller creature that moves onto it climbs on; if this one is the larger, it carries them and they move when it moves." },
  { value: "mount", label: "mount", hint: "Ridden by anything that moves onto it, whatever the sizes: a pony, a griffon, a wagon. Anyone who moves onto it is aboard at once, no question asked — that is what marking it a mount means." },
  { value: "under", label: "walks over others", hint: "Never carries anybody: creatures in its squares are underneath it, fighting from below. The tarrasque, the giant, the dragon overhead." },
  { value: "emanation", label: "emanation", hint: "A spell's area, not a creature: it follows the caster whose square it is in, stays centred on them, and never dismounts." },
];

export interface RiderToken { id: string; name: string; layer: string; left: number; top: number; width: number; height: number }

const KEY = "tokenRoles";
/** Roles are kept per campaign, keyed the way the tracker keys its rows: by
 *  token id when there is one, else by name (a row Roll20 has not bound yet). */
export const roleKeyFor = (t: { tokenId?: string; name: string }) => (t.tokenId && t.tokenId !== "-1" ? `t:${t.tokenId}` : `n:${String(t.name || "").replace(/\s+/g, " ").trim().toLowerCase()}`);
export const rolesOf = (layout: Layout): Record<string, TokenRole> => ((layout[KEY] as Record<string, Record<string, TokenRole>> | undefined)?.[layout.activeCampaign]) || {};
export const roleOf = (layout: Layout, t: { tokenId?: string; name: string }): TokenRole => rolesOf(layout)[roleKeyFor(t)] || "creature";
export function withRole(layout: Layout, t: { tokenId?: string; name: string }, role: TokenRole): Layout {
  const all = { ...((layout[KEY] as Record<string, Record<string, TokenRole>>) || {}) };
  const mine = { ...(all[layout.activeCampaign] || {}) };
  if (role === "creature") delete mine[roleKeyFor(t)]; else mine[roleKeyFor(t)] = role;
  all[layout.activeCampaign] = mine;
  return { ...layout, [KEY]: all };
}

/** How much map a token covers. Size, not "is it big": a 2x2 ogre covers four
 *  times a 1x1 goblin, and that ratio is what decides who carries whom. */
export const footprint = (t: RiderToken) => Math.max(0, t.width) * Math.max(0, t.height);
/** Is a's centre inside b's box? Standing ON something, not merely touching it. */
export const centreInside = (a: RiderToken, b: RiderToken) =>
  a.left > b.left - b.width / 2 && a.left < b.left + b.width / 2 && a.top > b.top - b.height / 2 && a.top < b.top + b.height / 2;

/** The base of a pile: the biggest thing in it. A mount wins over anything of
 *  its own size or smaller, because being ridden is its whole job. */
export function pickBase(tokens: RiderToken[], role: (t: RiderToken) => TokenRole): RiderToken | null {
  const candidates = tokens.filter((t) => role(t) !== "emanation" && role(t) !== "under");
  if (!candidates.length) return null;
  const rank = (t: RiderToken) => footprint(t) * (role(t) === "mount" ? 1.5 : 1);
  return candidates.slice().sort((a, b) => rank(b) - rank(a) || a.id.localeCompare(b.id))[0];
}

/** Where each rider sits on its base, as offsets from the base's centre.
 *  One sits in the middle; two side by side; three or more in a ring, kept
 *  inside the base's own square so nobody hangs off the edge. */
export function seatOffsets(n: number, base: { width: number; height: number }, riderSize: number): { dx: number; dy: number }[] {
  if (n <= 0) return [];
  if (n === 1) return [{ dx: 0, dy: 0 }];
  const span = Math.min(base.width, base.height);
  // the furthest a rider's CENTRE can sit and still be over the base
  const room = Math.max(0, (span - Math.min(riderSize, span)) / 2);
  if (n === 2) { const d = room > 0 ? room : span / 4; return [{ dx: -d, dy: 0 }, { dx: d, dy: 0 }]; }
  const r = room > 0 ? room : span / 3;
  return Array.from({ length: n }, (_, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;   // first seat at the top, then clockwise
    return { dx: Math.round(Math.cos(a) * r), dy: Math.round(Math.sin(a) * r) };
  });
}

/** Who is standing on whom, whatever they meant by it: the raw overlap, before
 *  intent is decided. Kept separate from `ridesToMake` because two creatures in
 *  one space can mean three different things — climbed on, walked under, or
 *  simply passing through — and the map cannot tell them apart. */
export interface Overlap { rider: RiderToken; base: RiderToken }
export function overlaps(tokens: RiderToken[], role: (t: RiderToken) => TokenRole, isEffect: (t: RiderToken) => boolean): Overlap[] {
  const creatures = tokens.filter((t) => (t.layer === "objects" || t.layer === "gmlayer") && !isEffect(t) && role(t) !== "emanation" && footprint(t) > 0);
  const out: Overlap[] = [];
  for (const rider of creatures) {
    const under = creatures.filter((b) => b.id !== rider.id && centreInside(rider, b) && (role(b) === "mount" || footprint(b) > footprint(rider)));
    const base = pickBase(under, role);
    if (base && base.id !== rider.id) out.push({ rider, base });
  }
  return out;
}
/** The move each rider needs to take its seat: current position -> seat.
 *  Returns only the ones that actually have to move, so a settled pile is quiet. */
export function seatMoves(base: RiderToken, riders: RiderToken[]): { id: string; dx: number; dy: number }[] {
  const size = Math.max(...riders.map((r) => Math.max(r.width, r.height)), 1);
  const seats = seatOffsets(riders.length, base, size);
  const out: { id: string; dx: number; dy: number }[] = [];
  riders.forEach((r, i) => {
    const dx = Math.round(base.left + seats[i].dx - r.left);
    const dy = Math.round(base.top + seats[i].dy - r.top);
    if (dx !== 0 || dy !== 0) out.push({ id: r.id, dx, dy });
  });
  return out;
}

// ---- moving into another creature's space ----
// A mount can still trample somebody, and somebody can still duck under a
// horse, so even a mount cannot be answered by its role alone. The question
// goes to whoever moved, in Roll20 chat, and it has three answers:
//   onto     climbed on / mounted. Linked and seated; moves with the base.
//   under    beneath it, fighting from below. Not linked; the tracker row
//            says so, so the DM can see who is under what at a glance.
//   through  passing through the space. Not linked — but ending the turn
//            there is a squeeze: prone, unless the other creature is two or
//            more sizes bigger (there is room to stand under a tarrasque).
export type MoveAnswer = "onto" | "under" | "through";
export interface MoveRecord { kind: MoveAnswer; riderId: string; riderName: string; baseId: string; baseName: string; at: string; turnId?: string; answeredBy?: string }
export const pairKey = (riderId: string, baseId: string) => `${riderId}>${baseId}`;
const MOVES = "tokenMoves";
export const movesOf = (layout: Layout): Record<string, MoveRecord> => ((layout[MOVES] as Record<string, Record<string, MoveRecord>> | undefined)?.[layout.activeCampaign]) || {};
export function withMove(layout: Layout, key: string, rec: MoveRecord | null): Layout {
  const all = { ...((layout[MOVES] as Record<string, Record<string, MoveRecord>>) || {}) };
  const mine = { ...(all[layout.activeCampaign] || {}) };
  if (rec) mine[key] = rec; else delete mine[key];
  all[layout.activeCampaign] = mine;
  return { ...layout, [MOVES]: all };
}
/** What the tracker row shows for a creature standing in someone else's space. */
export function moveNoteFor(layout: Layout, tokenId?: string): MoveRecord | null {
  if (!tokenId) return null;
  return Object.values(movesOf(layout)).find((m) => m.riderId === tokenId && m.kind !== "onto") || null;
}
/** Squares across, from a token's width on a page whose cell is `cellPx`. */
export const sizeIn = (t: { width: number }, cellPx: number) => Math.max(1, Math.round(t.width / Math.max(1, cellPx)));
/** Ending a turn in another creature's space: prone when it is the same size
 *  or one size bigger, because there is nowhere to stand. Two sizes or more
 *  and you are simply under it. */
export const proneForEndingThere = (riderSq: number, baseSq: number) => baseSq - riderSq <= 1;
